import { useRouter } from 'next/compat/router';
import { useStores } from 'contexts/stores';
import { PBB_NAME } from 'src/payments/constants';
import { usePbbSignupRedesignEnabled } from 'src/payments/dutchie-pay/flags';

type OpenDutchiePayType = {
  // undefined falls back to the cart dispensary; null means intentionally generic signup (no cname).
  cname?: string | null;
  step?: string;
  onCloseModal?: () => void;
  refHref?: string;
  enrollmentSource?: string;
};

// Return-context params stay gated on the UI variant allowlist (embedded variants own their
// navigation chrome); the cname does not — a known store must survive every variant.
const REF_URL_VARIANTS = ['default', 'mobile-ecomm', 'plus', 'embedded', 'store-front'];

export const useOpenDutchiePay = ({
  cname: cnameProp,
  step = '',
  onCloseModal,
  refHref,
  enrollmentSource = '',
}: OpenDutchiePayType): { openDutchiePay: (overrideStep?: string) => void } => {
  const router = useRouter();
  const { UI, Cart } = useStores();
  const cnameContextEnabled = usePbbSignupRedesignEnabled();
  const cartCname: string | undefined = Cart?.dispensary?.cName;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty-string cname must fall back too
  const callerCname = cnameProp === null ? undefined : cnameProp || cartCname;
  const cname = cnameContextEnabled ? callerCname : cartCname;

  const openDutchiePay = (overrideStep?: string): void => {
    const computedStep = overrideStep ?? step;
    const variantAllowlisted = REF_URL_VARIANTS.includes(UI.variant);
    // With the flag off, cname keeps its legacy scope: cart dispensary only, allowlisted variants only.
    const cnameAllowed = cnameContextEnabled ? true : variantAllowlisted;
    const queryOptions = {
      ...(cname && cnameAllowed && { cname }),
      ...(variantAllowlisted && {
        refUrl: refHref ?? router?.asPath,
        ...(computedStep && { step: computedStep }),
        ...(enrollmentSource && { enrollmentSource }),
      }),
    };

    if (UI.isMobileEcommApp) {
      // eslint-disable-next-line no-alert
      alert(`Log in to your account on dutchie.com to sign up for ${PBB_NAME}.`);
    } else {
      void router?.push({
        pathname: `/pay/signup`,
        query: queryOptions,
      });
      if (onCloseModal) {
        onCloseModal();
      }
    }
  };
  return { openDutchiePay };
};
